import { Stack } from 'react-bootstrap';
import { BsGeoAlt } from 'react-icons/bs';
import { Link } from 'react-router-dom';
import { DetailSection, LabelText } from '../../../../../../../components/generic';
import { IconLink } from '../../../../../../../components/iconLink/IconLink';
import StatusIcon from '../../../../../../../components/status/StatusIcon';
import { LinkCell } from '../../../../../../../components/tables/cellComponents/LinkCell';
import { getCurrentUser } from '../../../../../../../services/helper';
import { Course } from '../../../../../../../types/course.type';
import { displaySessionTimeRange } from '../../../../../../../utils/dateUtils';

const currentUser = getCurrentUser();

interface Props {
  course: Course | undefined;
}

export const SessionDetailsSection = ({ course }: Props) => {
  const chefPay =
    course?.pay_type === 'flat_rate' ? `Flat Rate $${course?.flat_rate}/day` : 'Hourly Rate';

  // Time display: render in the location's timezone, replicating v2 Rails'
  // Zone::FancyTime(ignore_dst: true) convention so displayed wall-clock
  // matches what v2 has shown parents for years (DST-stable, location-TZ).
  const tz = (course?.venue as any)?.location?.timezone;
  const timeDisplay = displaySessionTimeRange(course?.starts_at, course?.ends_at, tz) || null;

  // #16: Build a Google Maps URL from the venue's full address.
  const fullAddress = (() => {
    if (!course?.venue) return '';
    const parts = [
      course.venue.address,
      course.venue.city,
      course.venue.state,
      course.venue.zip
    ].filter(Boolean);
    return parts.join(', ');
  })();
  const mapsUrl = fullAddress
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`
    : null;

  return (
    <DetailSection title="Details">
      {/* #15 + #16: Single address render, pin icon (was phone), wrapped in Google Maps anchor */}
      <a
        href={mapsUrl ?? '#'}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          if (!mapsUrl) e.preventDefault();
        }}
        style={{ textDecoration: 'none', color: 'inherit' }}
      >
        <IconLink
          icon={<BsGeoAlt size={24} />}
          text={course?.venue?.title ?? course?.venue?.address}
        />
        <Stack
          className="text-info fw-semibold"
          style={{ marginLeft: 34 }}
        >
          <span>{course?.venue?.address}</span>
          <span>{`${course?.venue?.city ?? ''} ${course?.venue?.state ?? ''}, ${course?.venue?.zip ?? ''}`}</span>
        </Stack>
      </a>
      {Boolean(course?.is_online) && (
        <LabelText
          label="Video URL:"
          direction="vertical"
        >
          {course?.video_url?.trim() ? (
            <a
              href={course.video_url.trim()}
              target="_blank"
              rel="noopener noreferrer"
              className="text-info fw-bolder"
              style={{ fontSize: '.875rem', textDecoration: 'none' }}
            >
              {course.video_url.trim()}
            </a>
          ) : (
            <span className="fst-italic">No video URL saved</span>
          )}
        </LabelText>
      )}
      <LabelText
        label="Phone:"
        text={course?.venue?.phone}
      />
      <LabelText
        label="ID:"
        text={course?.id}
      />
      {!currentUser?.isInstructor && (
        <LabelText
          label="Theme:"
          children={
            <LinkCell
              content={course?.theme?.name ?? ''}
              url={`/admin/content/themes/${course?.theme?.id}`}
            />
          }
        />
      )}
      <LabelText
        label="Room:"
        text={course?.room}
      />
      <LabelText label="Time:">{timeDisplay}</LabelText>
      <LabelText
        label="Enrollment:"
        text={course?.enrollment}
      />
      {!currentUser?.isInstructor && (
        <LabelText
          label="Session Payment type:"
          text={course?.payment_type === 'onsite' ? 'On-site' : 'Website'}
        />
      )}
      {!currentUser?.isInstructor && (
        <LabelText
          label="Tuition:"
          direction="vertical"
          text={course?.tuition_summary_string}
        />
      )}
      <LabelText
        label="Chef Pay:"
        text={chefPay}
      />
      {course?.payment_type === 'website' && (
        <LabelText label="PRORATED:">
          <StatusIcon value={course?.can_be_prorated} />
        </LabelText>
      )}
      <LabelText label="Instructors:">
        <Stack>
          {course?.course_assignments.map((ca) => (
            <span key={ca.id}>
              {ca.instructor?.first_name} {ca.instructor?.last_name}
            </span>
          ))}
        </Stack>
      </LabelText>
      <LabelText
        label="Organizations:"
        direction="vertical"
      >
        {course?.venue?.organizations?.length ? (
          <Stack>
            {course.venue.organizations.map((org) => (
              <Link
                key={org.id}
                to={`/admin/places/organizations/${org.id}`}
                className="text-info fw-bold"
              >
                {org.name}
              </Link>
            ))}
          </Stack>
        ) : (
          <span className="fst-italic">No organizations are assigned to this venue</span>
        )}
      </LabelText>
    </DetailSection>
  );
};
